import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { Subscriptions_SubscriptionSyncResponseDados } from '../contracts/Subscriptions_SubscriptionSyncResponse'
import type { AiCredits_CreditPurchaseSyncResponseDados } from '../contracts/AiCredits_CreditPurchaseSyncResponse'
import type { SetupFee_SyncResponseDados } from '../contracts/SetupFee_SyncResponse'
// Import side-effects: registers the contracts with the contract registry.
// Both directions must be registered — an `inputContract` option naming a
// contract nobody imported fails the request with "is not registered" long
// before it reaches the network.
import '../contracts/Subscriptions_SubscriptionSyncResponse'
import '../contracts/Subscriptions_SubscriptionSyncRow'
import '../contracts/AiCredits_CreditPurchaseSyncResponse'
import '../contracts/Subscriptions_SubscriptionSyncInput'
import '../contracts/AiCredits_CreditPurchaseSyncInput'
import '../contracts/SetupFee_SyncResponse'
import '../contracts/SetupFee_SyncInput'


/**
 * useReconciliationSync — SOLE network owner for the two manual-reconciliation
 * routes (#27 backend, this round's macanetas):
 *
 *   syncSubscription(subscriptionId)  — POST subscriptions/admin/subscriptions/{id}/sync
 *     Reconciles the CYCLE charges of one subscription.
 *   syncCreditPurchases(franquiaId)   — POST ai/credits/admin/sync/{franquia_id}
 *     Reconciles the PENDING credit-pack purchases of one franchise.
 *
 * Two routes because a subscription and its franchise's credit purchases can
 * live on different gateways — see Subscriptions_SubscriptionSyncResponse's
 * docblock. Both return the SAME row shape (Subscriptions_SubscriptionSyncRow,
 * discriminated by `lane`), which is why they share one composable and feed
 * one result modal instead of two.
 *
 * REFUSAL DISCIPLINE: both backend routes refuse via sendError() (admin.php's
 * subscription_sync checks result['refused'], credits.php's sync uses
 * refuse(422, ...) on a bad franquia_id) and MY_Controller::sendResponse()/
 * sendError() NEVER touch the HTTP status — a refusal is HTTP 200 with the
 * envelope's `success:false`. `throwOnEnvelopeFailure` is the opt-in that
 * makes httpService itself reject on that envelope (see
 * useBillingThreeSixty.reprocessEvent() / useSubscriptionAdmin.extend() for
 * the same pattern). Do NOT read `dados.success` — it does not exist on this
 * response and would silently celebrate a refusal it can't see.
 */

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

export function useReconciliationSync() {
  /**
   * syncSubscription — POST subscriptions/admin/subscriptions/{id}/sync.
   * `dados.truncated` tells the caller the provider's charge listing was cut
   * by the reconciler's page cap — the caller MUST surface that, never show
   * a partial sweep as fully reconciled.
   */
  async function syncSubscription(subscriptionId: number): Promise<Subscriptions_SubscriptionSyncResponseDados> {
    const raw = await httpService.post(
      sistemaUrl(`subscriptions/admin/subscriptions/${subscriptionId}/sync`),
      {},
      {
        inputContract: 'Subscriptions_SubscriptionSyncInput',
        responseContract: 'Subscriptions_SubscriptionSyncResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'subscription_sync_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_SubscriptionSyncResponseDados }
    log('useReconciliationSync', 'syncSubscription', 'Assinatura reconciliada', {
      subscriptionId,
      rowCount: resp.dados?.rows?.length,
      truncated: resp.dados?.truncated,
    })
    return resp.dados
  }

  /**
   * syncCreditPurchases — POST ai/credits/admin/sync/{franquia_id}.
   * Scoped to a FRANCHISE, not a subscription — this lane's own gate is the
   * route itself (Full-only), never `can_reconcile` (that field is derived
   * from the SUBSCRIPTION's provider and does not apply here — see this
   * round's brief on the coupling trap).
   */
  async function syncCreditPurchases(franquiaId: number): Promise<AiCredits_CreditPurchaseSyncResponseDados> {
    const raw = await httpService.post(
      sistemaUrl(`ai/credits/admin/sync/${franquiaId}`),
      {},
      {
        inputContract: 'AiCredits_CreditPurchaseSyncInput',
        responseContract: 'AiCredits_CreditPurchaseSyncResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'credit_purchase_sync_failed',
      },
    )
    const resp = raw as { dados: AiCredits_CreditPurchaseSyncResponseDados }
    log('useReconciliationSync', 'syncCreditPurchases', 'Compras de créditos reconciliadas', {
      franquiaId,
      rowCount: resp.dados?.rows?.length,
    })
    return resp.dados
  }

  /**
   * syncSetupFee — POST subscriptions/setup-fee/sync/{id}.
   * Scoped to a FRANCHISE. Reconciles setup-fee charges against the provider.
   */
  async function syncSetupFee(franquiaId: number): Promise<SetupFee_SyncResponseDados> {
    const raw = await httpService.post(
      sistemaUrl(`subscriptions/setup-fee/sync/${franquiaId}`),
      {},
      {
        inputContract: 'SetupFee_SyncInput',
        responseContract: 'SetupFee_SyncResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'setup_fee_sync_failed',
      },
    )
    const resp = raw as { dados: SetupFee_SyncResponseDados }
    log('useReconciliationSync', 'syncSetupFee', 'Taxa de implantação reconciliada', {
      franquiaId,
      rowCount: resp.dados?.rows?.length,
    })
    return resp.dados
  }

  return {
    syncSubscription,
    syncCreditPurchases,
    syncSetupFee,
  }
}

// ── Code → t() key translation maps ─────────────────────────────────────────
//
// `action`/`detail` carry machine codes owned by the backend
// (SubscriptionReconciliationService::classify()/materializePending()), never
// UI copy. This table turns a code into a t() KEY — and in this repo the key
// IS the Portuguese string (see CLAUDE.md, `t('Nome do cliente')`), so the
// caller passes its own `t` and the label goes through the translation system
// like every other string on screen. Precedent: StaffSweepRunsModal.vue, which
// maps exactly this kind of backend vocabulary and wraps each label in t().
//
// The trap this must NOT be confused with: INTERPOLATING a raw backend code
// into a t() string (t('Falhou: {reason}')) once shipped a literal "{reason}"
// to the screen. Looking a code up and translating the resulting label is a
// different thing, and is the correct one.
//
// A code we don't recognize passes through RAW and is NOT sent to t() — that
// would log a missing-key warning for a string we never owned. Better an
// unfamiliar real backend code than one papered over as "erro".

const UNKNOWN_STATUS_PREFIX = 'unknown_status:'

/** action → pt-BR label. */
export const RECONCILIATION_ACTION_LABELS: Record<string, string> = {
  applied: 'Aplicado',
  already_applied: 'Já aplicado',
  materialized: 'Materializado',
  ignored: 'Ignorado',
  failed: 'Falhou',
}

/** detail → pt-BR label. `unknown_status:<STATUS>` is handled by prefix, not listed here. */
export const RECONCILIATION_DETAIL_LABELS: Record<string, string> = {
  revenue_row_not_found: 'Registro de receita não encontrado',
  revenue_row_ambiguous: 'Registro de receita ambíguo',
  provider_not_registered: 'Provedor não registrado',
  provider_does_not_support_reconciliation: 'Provedor não suporta reconciliação',
  credit_purchase_charge_in_subscription_listing: 'Cobrança de créditos apareceu na listagem da assinatura',
  not_a_credit_purchase: 'Não é uma compra de créditos',
  missing_charge_id: 'Sem identificador de cobrança',
  one_off_no_materialize: 'Cobrança avulsa não é materializada',
  charge_not_found_at_provider: 'Cobrança não encontrada no provedor',
  provider_get_charge_failed: 'Falha ao consultar a cobrança no provedor',
  duplicate_delivery: 'Entrega duplicada',
  provider_charge_id_missing: 'Cobrança sem identificador no provedor',
  no_status_map: 'Provedor sem vocabulário de status registrado',
  // Lane mismatches reachable since setup_fee became a real sync lane
  // (2026-08-21). `setup_fee_pull_not_mapped` was REMOVED with the stub that
  // emitted it: reconcileSetupChargesForFranchise() now queries the provider
  // like the other two lanes instead of reporting "not mapped".
  setup_fee_charge_in_cycle_listing: 'Cobrança de taxa de implantação apareceu na listagem da assinatura',
  setup_fee_charge_in_credit_purchase_listing: 'Cobrança de taxa de implantação apareceu na varredura de créditos',
  not_a_setup_fee_charge: 'Não é uma cobrança de taxa de implantação',
}


/** Translator the caller supplies — vue-i18n's `t` from useI18n(). */
type Translator = (key: string) => string

/**
 * translateReconciliationAction — action code → translated label.
 * An unrecognized code passes through RAW, untranslated — never a generic
 * fallback string, and never a t() lookup on a key we don't own.
 */
export function translateReconciliationAction(action: string, t?: Translator): string {
  const label = RECONCILIATION_ACTION_LABELS[action]
  if (label === undefined) return action
  return t ? t(label) : label
}

/**
 * translateReconciliationDetail — detail code → pt-BR label.
 * `unknown_status:<STATUS>` is a PREFIX + the provider's own raw status; the
 * status is preserved verbatim (we don't own that vocabulary, it's the
 * provider's). Any other unrecognized code passes through RAW, same rule as
 * translateReconciliationAction — a staff member reading an unfamiliar but
 * real backend code is better served than a code papered over as "erro".
 */
export function translateReconciliationDetail(
  detail: string | null | undefined,
  t?: Translator,
): string | null {
  if (detail == null) return null
  if (detail.startsWith(UNKNOWN_STATUS_PREFIX)) {
    const status = detail.slice(UNKNOWN_STATUS_PREFIX.length)
    const prefix = t ? t('Status desconhecido no provedor') : 'Status desconhecido no provedor'
    return `${prefix}: ${status}`
  }
  const label = RECONCILIATION_DETAIL_LABELS[detail]
  if (label === undefined) return detail
  return t ? t(label) : label
}
